/* eslint-disable max-classes-per-file */
import { urlApi } from '@/utils/url';

export interface DiagnosticConfig {
    domain: string;
    wss_url: string;
    user: string;
    password: string;
    echo_extension: string;
}

export class DiagnosticNotConfiguredError extends Error {}

/**
 * Resultado ya agregado de una ejecucion. El dominio, el mail, la red y la hora
 * de inicio no se mandan: el backend los saca del token, de la IP y de su reloj.
 */
export interface DiagnosticRunPayload {
    samples: number;
    status: 'completed' | 'cancelled' | 'failed';
    verdict?: string;
    loss_pct?: number;
    jitter_ms?: number;
    rtt_ms?: number;
    cut_seconds?: number;
    failed_step?: string;
    error_detail?: string;
    effective_type?: string;
    downlink?: number;
    user_agent: string;
    // IP publica con la que la central recibe el audio (candidato ICE en uso)
    media_ip?: string;
}

/** Una prueba del dia, como la ve cualquier usuario de la compañia. */
export interface DiagnosticRunRow {
    id: number;
    started_at: string;
    user_email: string;
    // running: en curso; abandoned: el navegador se cerro sin cerrarla
    status: 'running' | 'completed' | 'cancelled' | 'failed' | 'abandoned';
    verdict: string | null;
    loss_pct: number | null;
    jitter_ms: number | null;
    rtt_ms: number | null;
    cut_seconds: number | null;
    duration_seconds: number;
    failed_step: string | null;
    // IP publica con la que el servidor del panel vio al navegador
    client_ip: string | null;
    // IP publica con la que la central recibio el audio
    media_ip: string | null;
}

/** Pruebas del dia de todo el customer. El backend resuelve el dominio desde el token. */
export async function fetchDiagnosticRuns(): Promise<DiagnosticRunRow[]> {
    const token = sessionStorage.getItem('token') || '';
    const response = await fetch(`${urlApi}/diagnostic/results`, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        throw new Error(`Error ${response.status} cargando las pruebas del día`);
    }

    const body = await response.json();

    return body.data || [];
}

export interface DiagnosticEventRow {
    id: number;
    started_at: string;
    user_email: string;
    kind: string;
    seconds: number;
    peak: number | null;
}

/** Eventos del dia de todo el customer, del mas nuevo al mas viejo. */
export async function fetchDiagnosticEvents(): Promise<DiagnosticEventRow[]> {
    const token = sessionStorage.getItem('token') || '';
    const response = await fetch(`${urlApi}/diagnostic/events`, {
        method: 'GET',
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
        throw new Error(`Error ${response.status} cargando los eventos del día`);
    }

    const body = await response.json();

    return body.data || [];
}

export type DiagnosticBusyReason = 'user_busy' | 'domain_busy' | 'service_busy';

const BUSY_REASONS: DiagnosticBusyReason[] = ['user_busy', 'domain_busy', 'service_busy'];

/**
 * El servidor no dio cupo: el usuario ya tiene una prueba en curso, su compañia
 * llego a su tope o el panel llego al tope total de pruebas simultaneas.
 */
export class DiagnosticBusyError extends Error {
    constructor(readonly reason: DiagnosticBusyReason) {
        super(reason);
    }
}

function authHeaders(): Record<string, string> {
    const token = sessionStorage.getItem('token') || '';

    return {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
}

/**
 * Pide el cupo para una prueba y devuelve el id de la corrida. Sin cupo no se
 * debe registrar ni llamar: el servidor limita cuantas pueden correr a la vez
 * porque todas usan la misma extension contra la central.
 */
export async function startDiagnosticRun(
    durationSeconds: number,
    automatic = false,
): Promise<{ id: number; clientIp: string | null }> {
    const { connection } = navigator as any;
    /* eslint-disable @typescript-eslint/camelcase */
    const body = {
        duration_seconds: durationSeconds,
        automatic,
        user_agent: navigator.userAgent,
        effective_type: connection ? connection.effectiveType : undefined,
        downlink: connection ? connection.downlink : undefined,
    };
    /* eslint-enable @typescript-eslint/camelcase */
    const response = await fetch(`${urlApi}/diagnostic/runs`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
    });
    const parsed = await response.json().catch(() => ({}));

    if (response.status === 429) {
        const reason = parsed?.errors?.[0]?.diagnostic;
        throw new DiagnosticBusyError(BUSY_REASONS.includes(reason) ? reason : 'user_busy');
    }
    if (!response.ok) {
        throw new Error(`Error ${response.status} pidiendo cupo para la prueba`);
    }

    return { id: parsed.data.id, clientIp: parsed.data.client_ip || null };
}

/** Cierra la corrida con su resultado. Que no se pueda guardar no debe romper la pantalla: quien llama lo ignora. */
export async function finishDiagnosticRun(id: number, payload: DiagnosticRunPayload): Promise<void> {
    const response = await fetch(`${urlApi}/diagnostic/runs/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error(`Error ${response.status} guardando la prueba`);
    }
}

/**
 * Extension fija de diagnostico. El backend responde 503 cuando el secreto de
 * Secrets Manager no esta cargado en ese entorno: se distingue de una falla
 * de red para decirle al usuario que no es culpa de su conexion.
 */
export async function fetchDiagnosticConfig(): Promise<DiagnosticConfig> {
    const token = sessionStorage.getItem('token') || '';
    const response = await fetch(`${urlApi}/diagnostic`, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
        },
    });

    if (response.status === 503) {
        throw new DiagnosticNotConfiguredError('diagnostic not configured');
    }

    const body = await response.json();

    if (!response.ok) {
        throw new Error(`Error ${response.status}: ${JSON.stringify(body)}`);
    }

    return body.data as DiagnosticConfig;
}

export type DiagnosticEventKind = 'cut' | 'loss' | 'jitter' | 'latency' | 'dropped' | 'register_failed';

/** Algo que paso mientras se media: el backend le pone el dominio, el mail, la IP y la hora. */
export interface DiagnosticEventPayload {
    kind: DiagnosticEventKind;
    seconds: number;
    peak?: number;
    // hace cuantos segundos empezo
    ago_seconds: number;
    media_ip?: string;
    effective_type?: string;
}

/** Best effort: perder un evento no debe romper la medicion, quien llama ignora el error. */
export async function postDiagnosticEvents(events: DiagnosticEventPayload[]): Promise<void> {
    const response = await fetch(`${urlApi}/diagnostic/events`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ events }),
    });

    if (!response.ok) {
        throw new Error(`Error ${response.status} guardando los eventos`);
    }
}
